import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Lock, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";
import { evaluatePassword } from "@/lib/passwordPolicy";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ev = evaluatePassword(password);
    if (!ev.is_valid) { setError(ev.reasons[0] || "Contraseña insegura"); return; }
    if (password !== confirm) { setError("Las contrasenas no coinciden"); return; }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al restablecer la contrasena");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm rounded-sm border-slate-200">
          <CardContent className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <p className="text-sm text-slate-600">Token no proporcionado.</p>
            <Link to="/forgot-password">
              <Button variant="outline" className="rounded-sm text-xs uppercase mt-4" data-testid="request-new-token-btn">Solicitar nuevo enlace</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-sm rounded-sm border-slate-200" data-testid="reset-password-card">
        <CardHeader className="text-center pb-3">
          <CardTitle className="text-lg font-bold uppercase tracking-wider text-slate-900">Nueva Contrasena</CardTitle>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="text-center space-y-4 py-4" data-testid="reset-success">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
              <p className="text-sm text-slate-600">Contrasena restablecida correctamente.</p>
              <Link to="/login">
                <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase" data-testid="go-to-login-btn">Ir al login</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Nueva contrasena</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
                    className="pl-9 rounded-sm h-9 border-slate-200" data-testid="reset-password-input" maxLength={128} />
                </div>
                <PasswordStrengthMeter password={password} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Confirmar contrasena</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                    className="pl-9 rounded-sm h-9 border-slate-200" data-testid="reset-confirm-input" />
                </div>
              </div>
              {error && <p className="text-xs text-red-500" data-testid="reset-error">{error}</p>}
              <Button type="submit" disabled={loading}
                className="w-full bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold h-9" data-testid="reset-submit-btn">
                {loading ? "Guardando..." : "Restablecer contrasena"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
